import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Trophy, Instagram, Play, DollarSign } from 'lucide-react';
import { BloomInvitePanel } from '../components/BloomInvitePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const adminHeaders = () => {
  const key = localStorage.getItem('advecti_admin_key') || '';
  return { 'X-Admin-Key': key };
};

export default function AdminBloom() {
  const [connections, setConnections] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [adminKey, setAdminKey] = useState(localStorage.getItem('advecti_admin_key') || '');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [inviteRefresh, setInviteRefresh] = useState(0);

  const load = async () => {
    setLoading(true);
    setMsg('');
    try {
      const [c, u, d] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/bloom/connections`, { headers: adminHeaders() }),
        fetch(`${BACKEND_URL}/api/admin/bloom/upcoming`, { headers: adminHeaders() }),
        fetch(`${BACKEND_URL}/api/admin/bloom/drawings`, { headers: adminHeaders() }),
      ]);
      if (!c.ok || !u.ok || !d.ok) {
        setMsg('Auth failed. Enter admin key and click Load.');
        setLoading(false);
        return;
      }
      setConnections((await c.json()).connections || []);
      setUpcoming((await u.json()).upcoming || []);
      setDrawings((await d.json()).drawings || []);
      setInviteRefresh((value) => value + 1);
    } catch (e) {
      setMsg('Failed to load. ' + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    const stored = localStorage.getItem('advecti_admin_key') || '';
    if (stored) load();
  }, []);

  const forceDraw = async (advertiser_id, company_name) => {
    if (!window.confirm(`Force a Bloom drawing for ${company_name} now?`)) return;
    const r = await fetch(`${BACKEND_URL}/api/admin/bloom/draw/${advertiser_id}`, {
      method: 'POST', headers: adminHeaders(),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.ok) {
      alert('Draw failed: ' + (data.detail || data.reason || 'unknown'));
    } else {
      alert(`Drawing complete. Winner: ${data.drawing?.winner_handle ? '@' + data.drawing.winner_handle : '(no entries)'}`);
      load();
    }
  };

  const markPaid = async (drawing_id) => {
    const method = window.prompt('Payment method (e.g. "$100 Amazon gift card mailed 2/2/26"). Leave blank to cancel:', '');
    if (!method) return;
    const r = await fetch(`${BACKEND_URL}/api/admin/bloom/mark-paid/${drawing_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify({ payment_method: method, notes: '' }),
    });
    if (r.ok) load();
    else alert('Failed to mark paid');
  };

  const redraw = async (drawing_id, current_handle) => {
    const msg = current_handle
      ? `Void winner @${current_handle} and pick a new random entrant?\n\nUse this if the winner didn't actually follow both accounts.`
      : 'Redraw for this cycle?';
    if (!window.confirm(msg)) return;
    const r = await fetch(`${BACKEND_URL}/api/admin/bloom/redraw/${drawing_id}`, {
      method: 'POST', headers: adminHeaders(),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.ok) {
      alert('Redraw failed: ' + (data.detail || data.reason || 'unknown'));
      return;
    }
    if (data.new_winner) {
      alert(`New winner: @${data.new_winner}\n(${data.voided_count} previous winner${data.voided_count === 1 ? '' : 's'} voided · ${data.eligible_remaining} entrants remain eligible)`);
    } else {
      alert(data.reason || 'No eligible entrants remaining.');
    }
    load();
  };

  const setPrize = async (c) => {
    const prize = window.prompt(
      `Prize for @${c.ig_username} (e.g. "Free food for a year — one meal per week"). Leave blank to cancel:`,
      c.prize_description || '',
    );
    if (!prize) return;
    const r = await fetch(`${BACKEND_URL}/api/admin/bloom/prize/${c.advertiser_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify({ ig_user_id: c.ig_user_id, prize_description: prize }),
    });
    if (r.ok) load();
    else alert('Failed to set prize');
  };

  const setPostUrl = async (c) => {
    const url = window.prompt(
      `Giveaway post URL for @${c.ig_username} — where the QR code sends people.\nMust be a full https://www.instagram.com/... link:`,
      c.manual_pinned_url || '',
    );
    if (!url) return;
    const r = await fetch(`${BACKEND_URL}/api/admin/bloom/manual-pin/${c.advertiser_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify({ manual_pinned_url: url, ig_username: c.ig_username }),
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) load();
    else alert('Failed: ' + (d.detail || 'unknown'));
  };

  const backfill = async (c) => {
    if (!window.confirm(
      `Import past comments on @${c.ig_username}'s pinned post?\n\nInstagram only pushes comments posted after the app went live, so this catches everything from before. Safe to run more than once — duplicates are skipped.`
    )) return;
    setMsg(`Importing comments for @${c.ig_username}…`);
    const r = await fetch(`${BACKEND_URL}/api/admin/bloom/backfill/${c.advertiser_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...adminHeaders() },
      body: JSON.stringify({ ig_user_id: c.ig_user_id }),
    });
    const d = await r.json().catch(() => ({}));
    setMsg('');
    if (!r.ok) {
      alert('Import failed: ' + (d.detail || 'unknown'));
      return;
    }
    alert(`@${d.ig_username}: scanned ${d.scanned} comments\n${d.created} new entries\n${d.duplicate} already counted\n${d.no_tags} skipped (no friends tagged)`);
    load();
  };

  return (
    <div className="bg-cream-light min-h-screen py-10" data-testid="admin-bloom-page">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-pink-100 rounded-2xl p-3">
              <Sparkles className="text-pink-600" size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-navy">Bloom Attribution — Admin</h1>
              <p className="text-navy/60 text-sm">Manage connections, upcoming drawings, and winner payouts.</p>
            </div>
          </div>
          <nav className="text-sm text-navy/60 flex flex-wrap gap-4">
            <Link to="/admin/giveaways" className="hover:underline" data-testid="admin-bloom-email-giveaways">Email giveaways</Link>
            <Link to="/admin/advertisers" className="hover:underline">Advertisers</Link>
            <Link to="/admin/leads" className="hover:underline">Leads</Link>
            <Link to="/admin/sync-health" className="hover:underline">Sync Health</Link>
          </nav>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 flex items-center gap-3">
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            className="flex-1 min-w-0 px-3 py-2 border border-navy/20 rounded-lg text-sm"
            data-testid="admin-bloom-key-input"
          />
          <button
            onClick={() => { localStorage.setItem('advecti_admin_key', adminKey); load(); }}
            className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold"
            data-testid="admin-bloom-load-btn"
          >
            Load
          </button>
        </div>

        {msg && <div className="mb-4 text-sm text-red-600" data-testid="admin-bloom-msg">{msg}</div>}
        {loading && <div className="text-navy/60 py-4">Loading…</div>}

        <BloomInvitePanel adminKey={localStorage.getItem('advecti_admin_key') || ''} refreshKey={inviteRefresh} />

        <section className="border-b border-navy/10 pb-6 mb-8" data-testid="thunderdome-qr-downloads">
          <h2 className="text-base md:text-lg font-bold text-navy mb-3">Thunderdome print QR codes</h2>
          <div className="flex flex-wrap gap-x-8 gap-y-4 text-sm text-navy">
            {['eagle', 'bakersfield'].map((brand) => <div key={brand}>
              <span className="capitalize font-semibold mr-3" data-testid={`qr-label-${brand}`}>{brand === 'eagle' ? 'The Eagle' : 'Bakersfield'}</span>
              <a download href={`/wrap-assets/thunderdome/qr-${brand}-print.svg`} className="underline hover:text-pink-600 mr-3" data-testid={`qr-download-${brand}-svg`}>SVG</a>
              <a download href={`/wrap-assets/thunderdome/qr-${brand}-hires.png`} className="underline hover:text-pink-600" data-testid={`qr-download-${brand}-png`}>High-res PNG</a>
            </div>)}
            <a download href="/wrap-assets/thunderdome/thunderdome-qr-print-files.zip" className="underline hover:text-pink-600" data-testid="qr-download-zip">Download both (ZIP)</a>
          </div>
        </section>

        {/* Upcoming drawings */}
        <section className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="font-bold text-navy mb-4 flex items-center gap-2">
            <Trophy size={18} className="text-pink-600" /> Upcoming Drawings
          </h2>
          {upcoming.length === 0 ? (
            <p className="text-navy/60 text-sm">No upcoming drawings — no advertisers have an active cycle.</p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[720px]" data-testid="admin-bloom-upcoming-table">
              <thead className="text-navy/60 text-xs uppercase">
                <tr className="border-b border-navy/10">
                  <th className="text-left py-2">Advertiser</th>
                  <th className="text-left py-2">Cycle</th>
                  <th className="text-left py-2">Ends</th>
                  <th className="text-left py-2">Entries</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map((u) => (
                  <tr key={`${u.advertiser_id}-${u.cycle_index}`} className="border-b border-navy/5">
                    <td className="py-2 text-navy font-semibold">{u.company_name}</td>
                    <td className="py-2 text-navy/70">#{u.cycle_index}</td>
                    <td className="py-2 text-navy/70 text-xs">{u.cycle_end?.slice(0, 10)}</td>
                    <td className="py-2 text-navy/70">{u.entries}</td>
                    <td className="py-2">
                      {u.drawn ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Drawn</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Pending</span>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {!u.drawn && (
                        <button
                          onClick={() => forceDraw(u.advertiser_id, u.company_name)}
                          className="px-3 py-1.5 bg-pink-600 text-white rounded-lg text-xs font-semibold hover:bg-pink-700 inline-flex items-center gap-1"
                          data-testid={`admin-bloom-force-draw-${u.advertiser_id}`}
                        >
                          <Play size={12} /> Draw now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>

        {/* Connections */}
        <section className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="font-bold text-navy mb-4 flex items-center gap-2">
            <Instagram size={18} className="text-pink-600" /> Connected Instagram Accounts ({connections.length})
          </h2>
          {connections.length === 0 ? (
            <p className="text-navy/60 text-sm">No advertisers have connected Instagram yet.</p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[720px]" data-testid="admin-bloom-connections-table">
              <thead className="text-navy/60 text-xs uppercase">
                <tr className="border-b border-navy/10">
                  <th className="text-left py-2">Advertiser</th>
                  <th className="text-left py-2">IG Handle</th>
                  <th className="text-left py-2">Followers</th>
                  <th className="text-left py-2">Pinned</th>
                  <th className="text-left py-2">Prize</th>
                  <th className="text-left py-2">Webhook</th>
                  <th className="text-left py-2">Connected</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {connections.map((c) => (
                  <tr key={`${c.advertiser_id}-${c.ig_user_id}`} className="border-b border-navy/5 text-navy/80">
                    <td className="py-2 text-xs">{c.advertiser_id?.slice(0, 8)}…</td>
                    <td className="py-2"><a className="text-pink-600 hover:underline" href={`https://instagram.com/${c.ig_username}`} target="_blank" rel="noreferrer">@{c.ig_username}</a></td>
                    <td className="py-2">{(c.followers_count || 0).toLocaleString()}</td>
                    <td className="py-2 text-xs">{c.pinned_media_id || c.manual_pinned_url ? '✓' : '—'}</td>
                    <td className="py-2 text-xs max-w-[180px] truncate" title={c.prize_description || ''}>
                      {c.prize_description || <span className="text-navy/40">default $100</span>}
                    </td>
                    <td className="py-2 text-xs">{c.subscribed_at ? '✓' : '—'}</td>
                    <td className="py-2 text-xs">{c.connected_at?.slice(0, 10)}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setPrize(c)}
                        className="text-navy/70 hover:text-navy text-xs underline mr-3"
                        data-testid={`admin-bloom-set-prize-${c.advertiser_id}-${c.ig_username}`}
                      >
                        Prize
                      </button>
                      <button
                        onClick={() => setPostUrl(c)}
                        className="text-navy/70 hover:text-navy text-xs underline mr-3"
                        data-testid={`admin-bloom-set-post-${c.advertiser_id}-${c.ig_username}`}
                      >
                        Post URL
                      </button>
                      <button
                        onClick={() => backfill(c)}
                        className="text-pink-600 hover:text-pink-700 text-xs underline"
                        data-testid={`admin-bloom-backfill-${c.advertiser_id}-${c.ig_username}`}
                      >
                        Import past comments
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>

        {/* All drawings history */}
        <section className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="font-bold text-navy mb-4 flex items-center gap-2">
            <DollarSign size={18} className="text-pink-600" /> All Drawings ({drawings.length})
          </h2>
          {drawings.length === 0 ? (
            <p className="text-navy/60 text-sm">No drawings have been run yet.</p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[720px]" data-testid="admin-bloom-drawings-table">
              <thead className="text-navy/60 text-xs uppercase">
                <tr className="border-b border-navy/10">
                  <th className="text-left py-2">Drawn</th>
                  <th className="text-left py-2">Advertiser</th>
                  <th className="text-left py-2">Cycle</th>
                  <th className="text-left py-2">Winner</th>
                  <th className="text-left py-2">Entries</th>
                  <th className="text-left py-2">Prize</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {drawings.map((d) => (
                  <tr key={d.id} className="border-b border-navy/5 text-navy/80">
                    <td className="py-2 text-xs">{d.drawn_at?.slice(0, 10)}</td>
                    <td className="py-2">{d.advertiser_company || d.advertiser_id?.slice(0, 8)}</td>
                    <td className="py-2">#{d.cycle_index}</td>
                    <td className="py-2">
                      {d.winner_handle ? (
                        <a className="text-pink-600 hover:underline" href={`https://instagram.com/${d.winner_handle}`} target="_blank" rel="noreferrer">@{d.winner_handle}</a>
                      ) : <span className="text-navy/40 italic">no entries</span>}
                    </td>
                    <td className="py-2">{d.total_entries || 0}</td>
                    <td className="py-2">${d.prize_amount_usd}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        d.status === 'paid' ? 'bg-green-100 text-green-700' :
                        d.status === 'no_entries' ? 'bg-navy/10 text-navy/60' :
                        'bg-amber-100 text-amber-700'
                      }`}>{d.status}</span>
                    </td>
                    <td className="py-2 text-right">
                      {d.status === 'pending' && d.winner_handle && (
                        <button
                          onClick={() => redraw(d.id, d.winner_handle)}
                          className="px-3 py-1.5 mr-1 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600"
                          data-testid={`admin-bloom-redraw-${d.id}`}
                          title="Void this winner and pick another (use if winner didn't actually follow)"
                        >
                          Redraw
                        </button>
                      )}
                      {d.status === 'pending' && (
                        <button
                          onClick={() => markPaid(d.id)}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700"
                          data-testid={`admin-bloom-mark-paid-${d.id}`}
                        >
                          Mark paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
